import { useCallback, useRef, useState } from "react";
import { env } from "../config/env";
import { authStorage } from "../lib/storage/authStorage";

export type LiveStatus = "idle" | "connecting" | "listening" | "stopping" | "error";

interface LiveTranscriptState {
  status: LiveStatus;
  /** Committed/confirmed transcript segments so far, joined. */
  finalText: string;
  /** Not-yet-finalized tail — still being revised by the model. */
  interimText: string;
  errorMessage: string | null;
  totalDurationS: number | null;
  estimatedCostUsd: number | null;
}

const INITIAL_STATE: LiveTranscriptState = {
  status: "idle",
  finalText: "",
  interimText: "",
  errorMessage: null,
  totalDurationS: null,
  estimatedCostUsd: null,
};

function pickMimeType(): string {
  const candidates = ["audio/webm;codecs=opus", "audio/webm"];
  for (const type of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return "";
}

/**
 * True live, word-by-word captions over a persistent WebSocket to the
 * backend's Deepgram streaming proxy (/api/v1/stt/stream) — the same
 * mechanism Claude/ChatGPT voice mode uses (stream raw audio, get partial
 * transcripts back as you talk), not record-then-send.
 */
export function useLiveTranscription() {
  const [state, setState] = useState<LiveTranscriptState>(INITIAL_STATE);
  const wsRef = useRef<WebSocket | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const start = useCallback(async () => {
    setState({ ...INITIAL_STATE, status: "connecting" });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const wsBase = env.apiBaseUrl.replace(/^http/, "ws");
      const token = authStorage.getToken() || "";
      const ws = new WebSocket(`${wsBase}/api/v1/stt/stream?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;

      ws.onopen = () => {
        const mimeType = pickMimeType();
        const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
        recorderRef.current = recorder;
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0 && ws.readyState === WebSocket.OPEN) {
            void e.data.arrayBuffer().then((buf) => ws.send(buf));
          }
        };
        recorder.start(250); // emit a chunk every 250ms — small enough to feel live
        setState((s) => ({ ...s, status: "listening" }));
      };

      ws.onmessage = (event) => {
        let msg: any;
        try {
          msg = JSON.parse(event.data);
        } catch {
          return;
        }
        if (msg.type === "transcript") {
          setState((s) =>
            msg.is_final
              ? { ...s, finalText: `${s.finalText} ${msg.text}`.trim(), interimText: "" }
              : { ...s, interimText: msg.text },
          );
        } else if (msg.type === "closed") {
          setState((s) => ({
            ...s,
            totalDurationS: msg.total_duration_s ?? null,
            estimatedCostUsd: msg.estimated_cost_usd ?? null,
          }));
        } else if (msg.type === "error") {
          setState((s) => ({ ...s, status: "error", errorMessage: msg.message || "Stream error" }));
        }
      };

      ws.onerror = () => {
        setState((s) => (s.status === "listening" || s.status === "connecting"
          ? { ...s, status: "error", errorMessage: "Connection error" }
          : s));
      };

      ws.onclose = () => {
        setState((s) => (s.status === "error" ? s : { ...s, status: "idle" }));
      };
    } catch (err) {
      setState((s) => ({
        ...s,
        status: "error",
        errorMessage: err instanceof Error ? err.message : "Microphone access denied",
      }));
    }
  }, []);

  const stop = useCallback(() => {
    setState((s) => ({ ...s, status: "stopping" }));
    recorderRef.current?.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "stop" }));
    }
  }, []);

  return { ...state, start, stop };
}
